import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import NavBar from './components/NavBar';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import Dashboard from './components/Dashboard';
import RequestDemoForm from "./components/RequestDemoForm";
import DialogFlowEditor from "./components/DialogFlowEditor";
import SendCall from './components/SendCall';
import CallHistory from './components/CallHistory';
import CallDetails from './components/CallDetails';
import DialogFlowChat from './components/DialogFlowChat';
import DialogFlowChats from './components/DialogFlowChats';
import LandingPage from './components/LandingPage';
import ErrorBoundary from './components/ErrorBoundary';
import KnowledgeBasePage from './components/KnowledgeBasePage';
import Credits from './components/Credits';
import Contacts from './components/Contacts';
import Voices from './components/Voices';
import PhoneNumbers from './components/PhoneNumbers';
import TwilioManagement from './components/TwilioManagement';
import Campaigns from './components/Campaigns';
import CreateCampaignPage from './components/CreateCampaignPage';
import RequestDemoPage from './components/RequestDemoPage';
import WebAgents from './components/WebAgents';
import CustomTools from './components/CustomTools';
import AboutUs from './components/AboutUs';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import CookiePolicy from './components/legal/CookiePolicy';
import Solutions from './components/Solutions';
import Features from './components/Features';
import UseCases from './components/UseCases';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import BlogAdmin from './components/BlogAdmin';
import DemoVerification from './components/DemoVerification';
import DemoVerified from './components/DemoVerified';
import DemoAlreadyVerified from './components/DemoAlreadyVerified';
import DemoRequestSuccess from './components/DemoRequestSuccess';
import Settings from './components/Settings';

const App = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('app-sidebar-collapsed') === 'true';
  });

  useEffect(() => {
    window.localStorage.setItem('app-sidebar-collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            className: 'app-toast',
          }}
        />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/credits" element={<Navigate to="/app/credits" replace />} />
          <Route path="/request-demo" element={<RequestDemoPage />} />
          <Route path="/demo-request-success" element={<DemoRequestSuccess />} />
          <Route path="/verify-demo/:id/:token" element={<DemoVerification />} />
          <Route path="/demo-verified" element={<DemoVerified />} />
          <Route path="/demo-already-verified" element={<DemoAlreadyVerified />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/features" element={<Features />} />
          <Route path="/use-cases" element={<UseCases />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          
          {/* Protected Routes */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <div className={`app-shell min-h-screen ${isSidebarCollapsed ? 'app-sidebar-collapsed' : ''}`}>
                    <NavBar
                      isCollapsed={isSidebarCollapsed}
                      onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
                    />
                    <main className="app-main pt-16 md:pt-0">
                      <div className="app-content max-w-[1500px] mx-auto">
                        <Routes>
                          {/* Regular user dashboard */}
                          <Route path="/" element={<Dashboard />} />
                          
                          {/* Admin-only routes */}
                          <Route 
                            path="/admin" 
                            element={
                              <ProtectedRoute requireAdmin>
                                <AdminDashboard />
                              </ProtectedRoute>
                            } 
                          />
                          <Route 
                            path="/demo" 
                            element={
                              <ProtectedRoute requireAdmin>
                                <RequestDemoForm />
                              </ProtectedRoute>
                            } 
                          />
                          <Route 
                            path="/blog-admin" 
                            element={
                              <ProtectedRoute requireAdmin>
                                <BlogAdmin />
                              </ProtectedRoute>
                            } 
                          />
                          
                          {/* Common routes */}
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/dialog-flows" element={<DialogFlowEditor />} />
                          <Route path="/dialog-flows/:dialogFlowId" element={<DialogFlowEditor />} />
                          <Route path="/send-call" element={<SendCall />} />
                          <Route path="/campaigns" element={<Campaigns />} />
                          <Route path="/campaigns/create" element={<CreateCampaignPage />} />
                          <Route path="/calls" element={<CallHistory />} />
                          <Route path="/calls/:callId" element={<CallDetails />} />
                          <Route path="/dialog-flows/chats" element={<DialogFlowChats />} />
                          <Route path="/dialog-flows/:dialogFlowId/chat" element={<DialogFlowChat />} />
                          <Route path="/knowledge-bases" element={<KnowledgeBasePage />} />
                          <Route path="/credits" element={<Credits />} />
                          <Route path="/contacts" element={<Contacts />} />
                          <Route path="/voices" element={<Voices />} />
                          <Route path="/phone-numbers" element={<PhoneNumbers />} />
                          <Route path="/twilio-management" element={<TwilioManagement />} />
                          <Route path="/web-agents" element={<WebAgents />} />
                          <Route path="/custom-tools" element={<CustomTools />} />
                        </Routes>
                      </div>
                    </main>
                  </div>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
